'use client'

import type { MaestroImageProps } from '@maestro/commerce-blocks/components/image-product/image-product.types'
import type { ImageConfig } from '@maestro/commerce-blocks/theme/theme.types'

import classNames from 'classnames'

import { useImageProduct } from '@maestro/commerce-blocks/components/image-product/batons/use-image-product'
import ImagePlaceholder from '@maestro/commerce-blocks/components/image-product/image-placeholder'
import { theme } from '@maestro/local/theme'

import '@maestro/commerce-blocks/components/image-product/styles/image-product.scss'

export default function ImageProduct(props: MaestroImageProps) {
  const {
    src,
    imageConfig,
    className,
    fit = 'bounds',
    showPlaceholder = true,
    enableLazyLoading = true,
    enableFadeIn = true,
    forceIsLoaded, // for Storybook
    fetchPriority = 'auto',
    base64Blur,
    cachedUrl,
    loading = 'lazy',
    ...rest
  } = props

  const {
    width,
    height,
    srcSet: srcSetConfig,
    sizes: sizesConfig,
  }: ImageConfig = theme().images[imageConfig]

  const {
    finalIsLoaded,
    imageSrc,
    srcSet,
    preloadSrc,
    ref,
    setIsLoaded,
    sizes,
    showImage,
    canShowPlaceholder,
    shouldHideFinalImage,
  } = useImageProduct({
    src,
    height,
    fit,
    forceIsLoaded,
    className,
    enableLazyLoading,
    width,
    srcSetConfig,
    sizesConfig,
    base64Blur,
    cachedUrl,
    fetchPriority,
    showPlaceholder,
    enableFadeIn,
  })

  return (
    <div ref={ref} className={classNames(className, 'image-product')}>
      {canShowPlaceholder && (
        <ImagePlaceholder
          src={preloadSrc}
          blurDataUrl={base64Blur}
          cachedUrl={cachedUrl}
          cacheKey={src}
          width={width}
          height={height}
          isLoaded={finalIsLoaded}
          loading={loading}
        />
      )}
      {showImage && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          {...rest}
          className={classNames(
            'image-product__image',
            'image-product__image--final',
            { 'image-product__image--hide': shouldHideFinalImage }
          )}
          src={imageSrc}
          srcSet={srcSet}
          sizes={sizes}
          width={width}
          height={height}
          alt={props.alt || ''}
          onLoad={() => setIsLoaded(true)}
          loading={loading}
          fetchPriority={fetchPriority}
        />
      )}
    </div>
  )
}
